import { ReadonlyVec2 } from 'gl-matrix';
import { ContourTexture } from '../../data/structure/ContourTexture';
import {
    RegionOfInterest,
    TextureWrite,
} from '../../data/structure/RegionOfInterest';
import {
    BooleanOperation,
    SDFOperations,
} from '../../util/algorithms/sdf/SDFOperations';
import { TextureView } from '../../view/TextureView';
import { PlaneView } from '../../view/views/PlaneView';
import { EventHandlerResult } from '../EventHandlerResult';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { ContourAutoSuggestionController } from '~/engine/tool/contourAutoSuggestion/ContourAutoSuggestionController';
import { paneImageOf, PaneImageSource } from '~/engine/view/paneImage';
import { localization } from '~/i18n';
import { applyDrawingConstraintsToTexture } from './contouring-actions/constraintClipping';

/**
 * Drawing-constraint modifiers applied as a per-stroke SDF post-processing
 * step at commit, composing with every drawing tool. Both constraints only
 * affect additive (draw) operations — erasing removes content and can never
 * violate them.
 *
 *  - {@link bounding}: clip the stroke to the interior of this structure
 *    (`stroke = stroke ∩ bounding`).
 *  - {@link protectedStructures}: subtract these structures from the stroke
 *    so it cannot overwrite a protected neighbour
 *    (`stroke = stroke \ ⋃ protected`).
 */
export interface DrawingConstraints {
    /** Stay inside: clip the stroke to this structure's interior. */
    bounding?: RegionOfInterest;
    /** Stay outside: subtract these protected structures from the stroke. */
    protectedStructures?: RegionOfInterest[];
    /**
     * Intensity threshold: keep the stroke only on voxels whose underlying
     * image intensity falls within `[rawLower, rawUpper]`. Bounds are in the
     * image's **raw** voxel scale (already converted from the clinician-facing
     * display units — HU / SUVbw — by `displayRangeToRaw`), so the engine can
     * compare them directly against the image's `floatPixelData`. The image to
     * sample is the draw action's own `targetImage`.
     */
    intensityThreshold?: { rawLower: number; rawUpper: number };
}

/**
 * The reason a stroke kept to the
 * {@link DrawingConstraints.intensityThreshold} band refuses `view`, or
 * undefined when the view shows its Environment's primary image. The band
 * is set in the primary image's units, so on a view that draws another
 * image, or nothing, it would follow pixels the user does not see. A view
 * of an image locked to the primary is told so. Read in
 * the viewer's language at the time of the call.
 */
export function intensityStrokeRefusal(
    view: PaneImageSource
): string | undefined {
    if (paneImageOf(view).kind === 'primary') {
        return undefined;
    }
    // A pane showing an image locked to the primary is on the row of that
    // image already, so the way out is the primary's own row.
    return localization.t(
        view.alignedImageKey === undefined
            ? 'contouring.intensity.primaryImageOnly'
            : 'contouring.intensity.lockedRow'
    );
}

/**
 * The reason an adaptive stroke refuses `view`, or undefined when the view
 * shows an image: the stroke follows the intensities of the image the view
 * shows ({@link sampledImageOf}), so only a view that shows none has
 * nothing to follow. Read in the viewer's language at the time of the call.
 */
export function adaptiveStrokeRefusal(
    view: PaneImageSource
): string | undefined {
    return paneImageOf(view).kind === 'none'
        ? localization.t('contouring.intensity.noImageShown')
        : undefined;
}

/**
 * The image an adaptive stroke on `view` follows: the image the view shows,
 * placed where the view draws it, or `fallback` on a view that shows none.
 */
export function sampledImageOf(
    view: PaneImageSource,
    fallback: ImageObject
): ImageObject {
    const pane = paneImageOf(view);
    return pane.kind === 'none' ? fallback : pane.image;
}

/**
 * Whether a stroke keeps to the {@link DrawingConstraints.intensityThreshold}
 * band: a drawing stroke with a band set. An erase stroke ignores the band.
 */
export function strokeFollowsThreshold(
    constraints: DrawingConstraints | undefined,
    erase: boolean
): boolean {
    return !erase && constraints?.intensityThreshold !== undefined;
}

/**
 * {@link DrawActionState} stores the state of the active drawing action.\
 * {@link DrawActionState} is constructed when the user starts drawing and deleted when the drawing finishes.
 */
export abstract class DrawActionState {
    /** The structure the drawing is committed onto. */
    protected readonly _targetStructure: RegionOfInterest;

    protected readonly targetImage: ImageObject;

    protected readonly _erase: boolean = false;

    /**
     * Drawing-constraint modifiers applied to the stroke at commit. See
     * {@link DrawingConstraints}. Undefined for an erase stroke: erasing
     * removes content and so can never violate a constraint.
     */
    protected readonly _constraints?: DrawingConstraints;

    /** The {@link PlaneView} on which the drawing action occurred. */
    public readonly view: PlaneView;

    public constructor(
        view: PlaneView,
        targetStructure: RegionOfInterest,
        targetImage: ImageObject,
        erase: boolean,
        constraints?: DrawingConstraints
    ) {
        this.view = view;
        this._targetStructure = targetStructure;
        this.targetImage = targetImage;
        this._erase = erase;
        this._constraints = erase ? undefined : constraints;
    }

    public get targetStructure(): RegionOfInterest {
        return this._targetStructure;
    }

    /**
     * Method to apply the drawing.\
     * This renders the drawing on the target structure texture.
     */
    public applyBoolean(): void {
        const drawing = this.contourTexture;
        if (!drawing) {
            return;
        }
        let write: TextureWrite | undefined;
        let suggestionTakenIn = false;
        try {
            if (!this._targetStructure.texture) {
                return;
            }
            this.applyDrawingConstraints(drawing);
            write = this._targetStructure.startModify(this.actionName);
            const suggestions = ContourAutoSuggestionController.getInstance();
            // Constraints clip the stroke only, so a constrained stroke takes no suggestion in.
            suggestionTakenIn =
                !this._erase &&
                !this.isConstrained() &&
                (suggestions.pendingSuggestion?.views.includes(this.view) ??
                    false) &&
                this.addsContour(drawing) &&
                suggestions.includePendingIn(write, this.view);
            // Read the texture only now: a suggestion taken in replaces it.
            SDFOperations.applyBoolean(
                this._targetStructure.texture,
                drawing,
                this._erase ? BooleanOperation.AND_NOT : BooleanOperation.OR,
                this._targetStructure
            );
            write.finish();
        } catch (error) {
            // Cancelling undoes the taking in of the suggestion.
            if (write && (suggestionTakenIn || write.hasStagedTexture)) {
                write.cancel();
            }
            throw error;
        } finally {
            drawing.delete();
        }
    }

    private isConstrained(): boolean {
        const constraints = this._constraints;
        return (
            constraints?.bounding !== undefined ||
            constraints?.intensityThreshold !== undefined ||
            (constraints?.protectedStructures?.length ?? 0) > 0
        );
    }

    /** Not `isEmpty`, which is also true of a texture one voxel thick. */
    private addsContour(drawing: ContourTexture): boolean {
        return drawing.signedDistanceField.hasPixelsAbove(
            ContourTexture.SDF_THRESHOLD
        );
    }

    /**
     * Apply the configured {@link DrawingConstraints} to the stroke in place,
     * before it is committed onto the target — the single per-stroke SDF
     * post-processing step shared by every drawing tool:
     *  1. Bounding — intersect with the bounding structure (`drawing ∩ bounding`).
     *  2. Intensity threshold — intersect with the in-range voxel mask.
     *  3. Collision — subtract the protected structures (`drawing \ ⋃ protected`).
     *
     * Uses the same {@link constraintClipping} primitives as the live preview,
     * so preview and committed results stay identical. The constraint masks are
     * rasterized onto the (content-cropped) stroke's own grid, keeping the work
     * bounded to the stroke even when a bounding structure (e.g. BODY) spans the
     * whole image. Skipped when no constraint applies.
     */
    private applyDrawingConstraints(drawing: ContourTexture): void {
        applyDrawingConstraintsToTexture(
            drawing,
            this.targetImage,
            this._constraints,
            this._targetStructure.roiId
        );
    }

    /**
     * The finished drawing to commit onto the target structure, or
     * undefined when nothing was drawn. The caller owns the texture.
     */
    protected abstract get contourTexture(): ContourTexture | undefined;

    /**
     * Advance the drawing to a new mouse position.
     * @param coordinates The {@link ReadonlyVec2 client pixel coordinates} of the mouse move event.
     * @param view The {@link TextureView} on which the event occured.
     * @returns An {@link EventHandlerResult} informing how this {@link DrawActionState} reacted to the event and how to proceed.
     */
    public abstract onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: TextureView
    ): EventHandlerResult;

    /** Release every WebGL object this state holds. */
    public abstract clearMemory(): void;

    public abstract get actionName(): string;
}
